import ReactMarkdown from "react-markdown";

function Title({ children }) {
  return <h2 className="text-xl font-semibold mb-3">{children}</h2>;
}

// Hoisted outside the component to satisfy react/no-unstable-nested-components.
const MD_COMPONENTS = {
  h1: ({ node, ...p }) => <h3 className="text-base font-semibold mt-1 mb-2" {...p} />,
  h2: ({ node, ...p }) => <h3 className="text-base font-semibold mt-1 mb-2" {...p} />,
  h3: ({ node, ...p }) => <h4 className="text-sm font-semibold mt-1 mb-2" {...p} />,
  ul: ({ node, ...p }) => <ul className="list-disc ml-5 space-y-0.5 my-2" {...p} />,
  ol: ({ node, ...p }) => <ol className="list-decimal ml-5 space-y-0.5 my-2" {...p} />,
  p:  ({ node, ...p }) => <p className="my-2 leading-relaxed" {...p} />,
  strong: ({ node, ...p }) => <strong className="font-semibold text-slate-900" {...p} />,
  a:  ({ node, ...p }) => <a className="text-blue-600 underline" target="_blank" rel="noreferrer" {...p} />,
};

/** Render body_markdown with simple typographic styling. Used as a
 * fallback for any section_type when the structured `data` field is
 * empty — which is the case for legacy/seeded info_block, ppe_checklist
 * etc. sections whose content lives only in body_markdown. */
function MarkdownBody({ markdown }) {
  if (!markdown) return null;
  return (
    <div className="prose prose-sm max-w-none text-slate-700">
      <ReactMarkdown components={MD_COMPONENTS}>{markdown}</ReactMarkdown>
    </div>
  );
}

const isEmpty = (d) =>
  !d ||
  ((!d.rows || d.rows.length === 0) &&
   (!d.items || d.items.length === 0) &&
   (!d.routes || d.routes.length === 0) &&
   (!d.steps || d.steps.length === 0) &&
   (!d.files || d.files.length === 0) &&
   !d.image_url);

/**
 * Renders one site-rule section in its type-appropriate layout.
 * Falls back to body_markdown whenever the structured `data` is empty.
 * Read-only — used by both the public driver page and the admin preview.
 */
export default function SectionRenderer({ section }) {
  const d = section.data || {};
  const fallbackToMarkdown = isEmpty(d) && !!section.body_markdown;

  switch (section.section_type) {
    case "info_block":
      return (
        <section data-testid={`sec-${section.id}`}>
          <Title>{section.title}</Title>
          {fallbackToMarkdown ? (
            <MarkdownBody markdown={section.body_markdown} />
          ) : (
            <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-sm">
              {(d.rows || []).map((r, i) => (
                <div key={i} className="contents">
                  <dt className="text-slate-500">{r.label}</dt>
                  <dd className="font-medium">{r.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </section>
      );
    case "ppe_checklist":
      return (
        <section data-testid={`sec-${section.id}`}>
          <Title>{section.title}</Title>
          {fallbackToMarkdown ? (
            <MarkdownBody markdown={section.body_markdown} />
          ) : (
            <>
              <ul className="grid grid-cols-2 gap-1 text-sm">
                {(d.items || []).map((it, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    {it}
                  </li>
                ))}
              </ul>
              {d.other && <p className="text-xs text-slate-500 mt-2">Other: {d.other}</p>}
            </>
          )}
        </section>
      );
    case "travel_times":
      return (
        <section data-testid={`sec-${section.id}`}>
          <Title>{section.title}</Title>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-500 text-xs uppercase">
              <th className="pb-1">From</th><th>Route</th><th className="text-right">Minutes</th>
            </tr></thead>
            <tbody>
              {(d.rows || []).map((r, i) => (
                <tr key={i} className="border-t">
                  <td className="py-1.5">{r.origin}</td>
                  <td className="text-slate-600">{r.route}</td>
                  <td className="text-right tabular-nums">{r.minutes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      );
    case "approach":
      return (
        <section data-testid={`sec-${section.id}`}>
          <Title>{section.title}</Title>
          <div className="space-y-3">
            {(d.routes || []).map((r, i) => (
              <div key={i}>
                <div className="text-xs font-semibold text-slate-500 uppercase mb-1">From {r.from}</div>
                <ol className="list-decimal ml-5 text-sm space-y-0.5">
                  {(r.steps || []).map((s, j) => <li key={j}>{s}</li>)}
                </ol>
              </div>
            ))}
          </div>
        </section>
      );
    case "park_up_steps":
      return (
        <section data-testid={`sec-${section.id}`}>
          <Title>{section.title}</Title>
          <ol className="space-y-2 text-sm">
            {(d.steps || []).map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="font-mono text-xs bg-slate-900 text-white px-2 py-0.5 rounded">{s.action}</span>
                <span className="text-slate-700">{s.description}</span>
              </li>
            ))}
          </ol>
        </section>
      );
    case "accordion":
      return (
        <section data-testid={`sec-${section.id}`}>
          <Title>{section.title}</Title>
          <div className="space-y-3">
            {(d.items || []).map((it, i) => (
              <details key={i} open className="border rounded-md px-3 py-2">
                <summary className="cursor-pointer text-sm font-medium">{it.title}</summary>
                <div className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">{it.body}</div>
              </details>
            ))}
          </div>
        </section>
      );
    case "markdown":
      return (
        <section data-testid={`sec-${section.id}`}>
          <Title>{section.title}</Title>
          <MarkdownBody markdown={section.body_markdown} />
        </section>
      );
    case "file_upload":
      return (
        <section data-testid={`sec-${section.id}`}>
          <Title>{section.title}</Title>
          {fallbackToMarkdown && <MarkdownBody markdown={section.body_markdown} />}
          <ul className="text-sm space-y-1 mt-2">
            {(d.files || []).map((f, i) => (
              <li key={i}>
                <a className="text-blue-600 underline" href={f.url} target="_blank" rel="noreferrer">
                  {f.name || "View file"}
                </a>
              </li>
            ))}
          </ul>
        </section>
      );
    case "site_map":
      return d.image_url ? (
        <section data-testid={`sec-${section.id}`}>
          <Title>{section.title}</Title>
          <figure className="rounded-md border bg-white overflow-hidden">
            <img
              src={d.image_url}
              alt={section.title || "Site map"}
              className="w-full max-h-[600px] object-contain bg-slate-50"
              data-testid={`site-map-image-${section.id}`}
            />
            {d.caption && (
              <figcaption className="text-xs text-slate-600 px-3 py-2 border-t bg-slate-50">
                {d.caption}
              </figcaption>
            )}
          </figure>
        </section>
      ) : null;
    default:
      // Unknown section_type — render title + body_markdown if present so
      // content is never silently lost.
      if (section.body_markdown) {
        return (
          <section data-testid={`sec-${section.id}`}>
            <Title>{section.title}</Title>
            <MarkdownBody markdown={section.body_markdown} />
          </section>
        );
      }
      return null;
  }
}
